import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { Crown, Pencil, Plus, Shield, Trash2, UserMinus, UserPlus } from 'lucide-react'
import { type FormEvent, useEffect, useState } from 'react'
import { PersonnelLayout } from '../components/personnel-layout'
import { MobileBottomSlots } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus } from '../components/ui/mobile-action-bar'
import { Select } from '../components/ui/select'
import { SidePanel } from '../components/ui/side-panel'
import { api } from '../lib/api'
import { notify } from '../lib/notify'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/teams',
  component: TeamsPage,
})

type Team = {
  id: string
  name: string
  description: string | null
  archived: boolean
  members: { userId: string; role: string; name: string }[]
}

function TeamsPage() {
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<Team | null>(null)
  const [memberDialog, setMemberDialog] = useState<{ open: boolean; team: Team | null }>({
    open: false,
    team: null,
  })

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })
  const { data: teamsData, isLoading } = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.listTeams(),
    enabled: !!meData?.user,
  })
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.listUsers(),
    enabled: meData?.user?.role === 'admin',
  })
  const { data: teamMembersData } = useQuery({
    queryKey: ['team-members'],
    queryFn: () => api.listTeamMembers(),
    enabled: !!meData?.user,
  })

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />

  const isAdmin = meData.user.role === 'admin'
  const teams = teamsData?.teams ?? []
  // For non-admin, fall back to /team-members for the picker (no email/rate)
  const allUsers =
    usersData?.users ?? teamMembersData?.users.map((u) => ({ ...u, archived: false })) ?? []

  return (
    <PersonnelLayout
      title="Teams"
      description="Team-Leiter dürfen Abwesenheiten und Zeit-Einträge ihrer Mitglieder bearbeiten und löschen, ohne Admin zu sein."
    >
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): ＋ Neues Team in der Bottom-Bar — derselbe
          Handler + Admin-Gate wie der Kopf-Button. */}
      {isAdmin && (
        <MobileBottomSlots>
          <BarAccentPlus ariaLabel="Neues Team" onClick={() => setCreateOpen(true)} />
        </MobileBottomSlots>
      )}

      <div className="space-y-6">
        {/* <640 übernimmt die Bottom-Bar — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
        <div className="flex phone:hidden justify-end">
          {isAdmin && (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Neues Team
            </Button>
          )}
        </div>

        {isLoading ? (
          <p className="text-text-secondary">Lade …</p>
        ) : teams.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border-default bg-bg-elevated/30 px-6 py-16 text-center">
            <p className="text-text-secondary">
              {isAdmin
                ? 'Noch keine Teams. Leg das erste an.'
                : 'Du gehörst noch keinem Team an. Frag deinen Admin.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {teams.map((t) => (
              <TeamCard
                key={t.id}
                team={t}
                isAdmin={isAdmin}
                isLeader={t.members.find((m) => m.userId === meData.user.id)?.role === 'leader'}
                onEdit={() => setEditing(t)}
                onManageMembers={() => setMemberDialog({ open: true, team: t })}
              />
            ))}
          </div>
        )}
      </div>

      {isAdmin && (
        <TeamFormDialog
          open={createOpen || !!editing}
          team={editing}
          onClose={() => {
            setCreateOpen(false)
            setEditing(null)
          }}
        />
      )}

      <MembersDialog
        open={memberDialog.open}
        team={memberDialog.team}
        allUsers={allUsers}
        currentUserId={meData.user.id}
        isAdmin={isAdmin}
        onClose={() => setMemberDialog({ open: false, team: null })}
      />
    </PersonnelLayout>
  )
}

function TeamCard({
  team,
  isAdmin,
  isLeader,
  onEdit,
  onManageMembers,
}: {
  team: Team
  isAdmin: boolean
  isLeader: boolean
  onEdit: () => void
  onManageMembers: () => void
}) {
  const queryClient = useQueryClient()
  const remove = useMutation({
    mutationFn: () => api.deleteTeam(team.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] })
      notify.success('Team gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const leaders = team.members.filter((m) => m.role === 'leader')
  const members = team.members.filter((m) => m.role === 'member')
  const canManageMembers = isAdmin || isLeader

  return (
    <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-medium text-text-primary">{team.name}</h2>
          {team.description && (
            <p className="mt-1 text-sm text-text-secondary">{team.description}</p>
          )}
        </div>
        <div className="flex items-center gap-1">
          {isAdmin && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onEdit}
              aria-label="Team bearbeiten"
              className="text-text-tertiary hover:text-accent"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          {isAdmin && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={async () => {
                const ok = await confirm({
                  title: `Team „${team.name}“ löschen?`,
                  description:
                    'Mitgliedschaften werden mitgelöscht (User-Accounts bleiben erhalten).',
                  variant: 'danger',
                  confirmLabel: 'Löschen',
                })
                if (ok) remove.mutate()
              }}
              aria-label="Team löschen"
              className="text-text-tertiary hover:text-danger"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {leaders.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-2xs uppercase tracking-wider text-text-tertiary">Team-Leiter</p>
          <div className="flex flex-wrap gap-2">
            {leaders.map((m) => (
              <MemberPill key={m.userId} name={m.name} kind="leader" />
            ))}
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <p className="text-2xs uppercase tracking-wider text-text-tertiary">
          Mitglieder ({members.length})
        </p>
        {members.length === 0 ? (
          <p className="text-xs text-text-tertiary italic">noch keine Mitglieder</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {members.map((m) => (
              <MemberPill key={m.userId} name={m.name} kind="member" />
            ))}
          </div>
        )}
      </div>

      {canManageMembers && (
        <div className="pt-2 border-t border-border-subtle flex justify-end">
          <Button variant="secondary" size="sm" onClick={onManageMembers}>
            <UserPlus className="h-4 w-4" /> Mitglieder verwalten
          </Button>
        </div>
      )}
    </div>
  )
}

function MemberPill({ name, kind }: { name: string; kind: 'leader' | 'member' }) {
  const isLeader = kind === 'leader'
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs border',
        isLeader
          ? 'border-accent/40 bg-accent/10 text-accent'
          : 'border-border-default bg-bg-overlay/50 text-text-secondary',
      )}
    >
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-accent/15 text-2xs font-semibold text-accent">
        {name.slice(0, 2).toUpperCase()}
      </span>
      {name}
      {isLeader && <Crown className="h-3 w-3" strokeWidth={2.5} />}
    </span>
  )
}

function TeamFormDialog({
  open,
  team,
  onClose,
}: {
  open: boolean
  team: Team | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const isEdit = !!team
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  useEffect(() => {
    if (!open) return
    if (team) {
      setName(team.name)
      setDescription(team.description ?? '')
    } else {
      setName('')
      setDescription('')
    }
  }, [open, team])

  const save = useMutation({
    mutationFn: () => {
      if (isEdit && team) {
        return api.updateTeam(team.id, { name, description: description || null })
      }
      return api.createTeam({ name, description: description || null })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] })
      notify.success(isEdit ? 'Team aktualisiert' : 'Team angelegt')
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    save.mutate()
  }

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={isEdit ? `${team?.name} bearbeiten` : 'Neues Team'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button onClick={onSubmit} type="button" disabled={!name.trim() || save.isPending}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4 pb-2">
        <div className="space-y-2">
          <Label htmlFor="team-name">Name</Label>
          <Input
            id="team-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z.B. Backend-Team, Sales, Design"
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="team-desc">Beschreibung (optional)</Label>
          <Input
            id="team-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Zweck des Teams, Standort, …"
          />
        </div>
      </form>
    </SidePanel>
  )
}

function MembersDialog({
  open,
  team,
  allUsers,
  currentUserId,
  isAdmin,
  onClose,
}: {
  open: boolean
  team: Team | null
  allUsers: { id: string; name: string }[]
  currentUserId: string
  isAdmin: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [pickedUserId, setPickedUserId] = useState('')
  const [pickedRole, setPickedRole] = useState<'leader' | 'member'>('member')

  useEffect(() => {
    if (!open) return
    setPickedUserId('')
    setPickedRole('member')
  }, [open])

  const add = useMutation({
    mutationFn: () =>
      team ? api.addTeamMember(team.id, pickedUserId, pickedRole) : Promise.reject(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] })
      setPickedUserId('')
      notify.success('Mitglied hinzugefügt')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const updateRole = useMutation({
    mutationFn: (vars: { userId: string; role: 'leader' | 'member' }) =>
      team ? api.updateTeamMemberRole(team.id, vars.userId, vars.role) : Promise.reject(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] })
      notify.success('Rolle aktualisiert')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const remove = useMutation({
    mutationFn: (userId: string) =>
      team ? api.removeTeamMember(team.id, userId) : Promise.reject(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] })
      notify.success('Mitglied entfernt')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  if (!team) return null

  const memberIds = new Set(team.members.map((m) => m.userId))
  const candidates = allUsers.filter((u) => !memberIds.has(u.id))

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={`${team.name} – Mitglieder`}
      description="Leiter dürfen Abwesenheiten und Zeit-Einträge ihrer Mitglieder bearbeiten."
      footer={
        <Button variant="ghost" onClick={onClose} type="button">
          Schließen
        </Button>
      }
    >
      <div className="space-y-5 pb-2">
        <div className="rounded-xl border border-border-subtle bg-bg-overlay/30 p-3 space-y-3">
          <p className="text-xs uppercase tracking-wider text-text-tertiary">Mitglied hinzufügen</p>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-2">
            <Select value={pickedUserId} onChange={(e) => setPickedUserId(e.target.value)}>
              <option value="">— Person wählen —</option>
              {candidates.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </Select>
            <Select
              value={pickedRole}
              onChange={(e) => setPickedRole(e.target.value as 'leader' | 'member')}
            >
              <option value="member">Mitglied</option>
              <option value="leader">Leiter</option>
            </Select>
            <Button
              onClick={() => add.mutate()}
              type="button"
              disabled={!pickedUserId || add.isPending}
            >
              <UserPlus className="h-4 w-4" /> Hinzufügen
            </Button>
          </div>
          {candidates.length === 0 && (
            <p className="text-xs text-text-tertiary italic">
              Alle aktiven Mitarbeiter sind bereits im Team.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <p className="text-xs uppercase tracking-wider text-text-tertiary">Aktuelle Besetzung</p>
          {team.members.length === 0 ? (
            <p className="text-sm text-text-secondary italic">Noch leer.</p>
          ) : (
            <ul className="divide-y divide-border-subtle rounded-xl border border-border-subtle">
              {team.members.map((m) => {
                const isSelf = m.userId === currentUserId
                return (
                  <li key={m.userId} className="flex items-center gap-3 px-3 py-2.5">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent/15 text-accent text-2xs font-semibold">
                      {m.name.slice(0, 2).toUpperCase()}
                    </div>
                    <span className="flex-1 text-sm text-text-primary">
                      {m.name}
                      {isSelf && <span className="ml-1.5 text-xs text-text-tertiary">· du</span>}
                    </span>
                    <Select
                      value={m.role}
                      onChange={(e) =>
                        updateRole.mutate({
                          userId: m.userId,
                          role: e.target.value as 'leader' | 'member',
                        })
                      }
                      className="h-8 w-32"
                    >
                      <option value="member">Mitglied</option>
                      <option value="leader">Leiter</option>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={async () => {
                        const ok = await confirm({
                          title: 'Aus Team entfernen?',
                          description: m.name,
                          variant: 'danger',
                          confirmLabel: 'Entfernen',
                        })
                        if (ok) remove.mutate(m.userId)
                      }}
                      aria-label="Entfernen"
                      className="text-text-tertiary hover:text-danger"
                    >
                      <UserMinus className="h-4 w-4" />
                    </Button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        {!isAdmin && (
          <p className="text-xs text-text-tertiary flex items-start gap-2">
            <Shield className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Du bist Leiter dieses Teams. Du kannst Mitglieder hinzufügen, ihre Rolle ändern und
            entfernen — aber nur Admins legen neue Teams an oder löschen sie.
          </p>
        )}
      </div>
    </SidePanel>
  )
}
